import React, { Key, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { HorizontalLoader, ModalWrapper } from '../../../../Components'
import Pagination from '../../../../Components/Pagination/Pagination'
import { Logger } from '../../../../Helpers/Logger'
import posService from '../../../../Services/posService'
import { PosAccountType } from 'Models/posAccountType'
import './PosAccounts.scss'
import { TimeConverter } from 'Helpers/TimeConverter'
import SearchInput from 'Components/SearchInput/SearchInput'
import { checkSearchTransactionInputType } from 'Helpers'
import Swal from 'sweetalert2'
import { toast, ToastContainer } from 'react-toastify'
import { handleLoading } from 'Helpers/alert/alerts'

const PosAccounts = () => {
  const { terminalId } = useParams<{ terminalId: string }>()
  const [pageNumber, setPageNumber] = useState(1)
  const [sortOrder, setSortOrder] = useState('DESC')
  const [accounts, setAccounts] = useState<PosAccountType[]>([])
  const [loading, setLoading] = useState(false)
  const [paginationData, setPaginationData] = useState<{
    pageNumber: number
    pageSize: number
    total: number
  }>({
    pageNumber: 1,
    pageSize: 20,
    total: 1,
  })

  const [searching, setSearching] = useState(false)
  const [isSearching, setIsSearching] = useState(false)
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [from, setFrom] = useState<string | undefined>()
  const [to, setTo] = useState<string | undefined>()
  const [posTerminalId, setPosTerminalId] = useState<string>()
  const [provider, setProvider] = useState<string | undefined>()
  const [showUpdateModal, setShowUpdateModal] = useState(false)
  const [selectedAccount, setSelectedAccount] = useState<PosAccountType | null>(null)
  const [authorizationCode, setAuthorizationCode] = useState('')
  const [updateLoading, setUpdateLoading] = useState(false)
  const [updateError, setUpdateError] = useState('')

  const getAccounts = async () => {
    setLoading(true)
    await posService
      .getAccountsWithPosTerminal(
        pageNumber,
        sortOrder,
        terminalId?.toUpperCase(),
        from,
        to,
        posTerminalId,
        provider,
      )
      .then(
        (response) => {
          setLoading(false)
          setAccounts(response?.data.data.dataset)
          setPaginationData({
            pageNumber: response?.data.data.pageNumber,
            pageSize: response?.data.data.pageSize,
            total: response?.data.data.total,
          })
        },
        (error: { response: object }) => {
          setLoading(false)
          Logger('response error', error.response)
        },
      )
  }

  useEffect(() => {
    getAccounts()
  }, [sortOrder, pageNumber, terminalId, from, to, provider])

  const handleSearch = async (searchTermRaw: string) => {
    const searchTerm = searchTermRaw.trim()

    if (!searchTerm) {
      setIsSearching(false)
      setSearchTerm('')
      setPageNumber(1)
      getAccounts()
      return
    }

    if (searching) {
      return
    }

    setSearching(true)
    setIsSearching(true)
    setPageNumber(1)
    setSearchTerm(searchTerm)

    const searchTermRes = checkSearchTransactionInputType(searchTerm)

    let endpoint = ''
    if (searchTermRes === 'PhoneNumber') {
      endpoint = `customerMsisdn=${encodeURIComponent(searchTerm)}`
    } else if (searchTermRes === 'name') {
      endpoint = `posTerminalId=${encodeURIComponent(searchTerm)}`
    }

    if (endpoint === '') {
      getAccounts()
      setSearching(false)
      return
    }

    setLoading(true)
    try {
      const response: any = await posService.searchPosAccount(endpoint, pageNumber, sortOrder)
      setAccounts(response?.data.data.dataset)
      setPaginationData({
        pageNumber: response?.data.data.pageNumber,
        pageSize: response?.data.data.pageSize,
        total: response?.data.data.total,
      })
    } catch (error: any) {
      setAccounts([])
      getAccounts()
      Logger('response error', error.response)
    } finally {
      setLoading(false)
      setSearching(false)
    }
  }

  const fetchTransactions = () => {
    if (isSearching && searchTerm) {
      handleSearch(searchTerm)
    } else {
      getAccounts()
    }
  }

  useEffect(() => {
    fetchTransactions()
  }, [sortOrder, pageNumber, terminalId, provider])

  const deleteAccount = async (id: string) => {
    Swal.fire({
      title: 'Confirm!',
      text: 'Are you sure you want to delete this POS terminal?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#186AFF',
      cancelButtonColor: '#d33',
      confirmButtonText: `Yes, ${status === 'CANCELLED' ? 'cancel' : 'confirm'}!`,
    }).then(async (result) => {
      if (result.isConfirmed) {
        const loading = handleLoading('Processing please wait...')

        await posService.deleteAccountPosTerminal(id).then(
          () => {
            toast.dismiss(loading)
            Swal.fire('Done!', 'POS terminal deleted succesfully', 'success')
            getAccounts()
          },
          (error) => {
            toast.dismiss(loading)
            Swal.fire({
              position: 'center',
              icon: 'warning',
              text: error.response.data.error,
              showConfirmButton: false,
              timer: 5000,
            })
          },
        )
      }
    })
  }

  const openUpdateTerminalModal = (account: PosAccountType) => {
    setSelectedAccount(account)
    setAuthorizationCode(account.authorizationCode ?? '')
    setUpdateError('')
    setShowUpdateModal(true)
  }

  const closeUpdateTerminalModal = () => {
    if (updateLoading) return
    setShowUpdateModal(false)
    setSelectedAccount(null)
    setAuthorizationCode('')
    setUpdateError('')
  }

  const updateTerminalAuthorizationCode = async () => {
    if (!selectedAccount) return

    const nextAuthorizationCode = authorizationCode.trim()
    if (!nextAuthorizationCode) {
      setUpdateError('Authorization code is required')
      return
    }

    setUpdateLoading(true)
    setUpdateError('')

    await posService
      .updateAccountPosTerminal(selectedAccount.id, {
        authorizationCode: nextAuthorizationCode,
      })
      .then(
        () => {
          setUpdateLoading(false)
          closeUpdateTerminalModal()
          Swal.fire('Done!', 'POS terminal updated successfully', 'success')
          fetchTransactions()
        },
        (error) => {
          setUpdateLoading(false)
          setUpdateError(error?.response?.data?.error || 'Unable to update terminal')
        },
      )
  }

  return (
    <div className='posAccount container mt-4'>
      <div className='card border-0'>
        <div className='card-header border-0'>
          <div className='row'>
            <div className='col-12 col-md-8 my-auto'>
              <div className='title'>POS Terminals - {terminalId?.toUpperCase()}</div>
            </div>
          </div>
        </div>
      </div>
      <div className='card border-0'>
        <div className='card-header border-0 px-0 '>
          <div className='row mt-2'>
            <div className='col-12 col-md-10'>
              <SearchInput
                onSearch={handleSearch}
                placeholder={'Search by POS Terminal ID or Customer MSISDN'}
              />
            </div>
            <div className='col-6 col-md-2 dropdown'>
              <button
                className='btn btn-filter dropdown-toggle'
                data-bs-toggle='dropdown'
                aria-expanded='false'
              >
                <svg
                  width='17'
                  height='17'
                  viewBox='0 0 24 24'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    d='M10.94 22.65C10.46 22.65 9.99002 22.53 9.55002 22.29C8.67002 21.8 8.14002 20.91 8.14002 19.91V14.61C8.14002 14.11 7.81002 13.36 7.50002 12.98L3.76002 9.02001C3.13002 8.39001 2.65002 7.31001 2.65002 6.50001V4.20001C2.65002 2.60001 3.86002 1.35001 5.40002 1.35001H18.6C20.12 1.35001 21.35 2.58001 21.35 4.10001V6.30001C21.35 7.35001 20.72 8.54001 20.13 9.13001L15.8 12.96C15.38 13.31 15.05 14.08 15.05 14.7V19C15.05 19.89 14.49 20.92 13.79 21.34L12.41 22.23C11.96 22.51 11.45 22.65 10.94 22.65ZM5.40002 2.85001C4.70002 2.85001 4.15002 3.44001 4.15002 4.20001V6.50001C4.15002 6.87001 4.45002 7.59001 4.83002 7.97001L8.64002 11.98C9.15002 12.61 9.65002 13.66 9.65002 14.6V19.9C9.65002 20.55 10.1 20.87 10.29 20.97C10.71 21.2 11.22 21.2 11.61 20.96L13 20.07C13.28 19.9 13.56 19.36 13.56 19V14.7C13.56 13.63 14.08 12.45 14.83 11.82L19.11 8.03001C19.45 7.69001 19.86 6.88001 19.86 6.29001V4.10001C19.86 3.41001 19.3 2.85001 18.61 2.85001H5.40002Z'
                    fill='#868484'
                  />
                  <path
                    d='M6.00004 10.75C5.86004 10.75 5.73004 10.71 5.60004 10.64C5.25004 10.42 5.14004 9.94999 5.36004 9.59999L10.29 1.69999C10.51 1.34999 10.97 1.23999 11.32 1.45999C11.67 1.67999 11.78 2.13999 11.56 2.48999L6.63004 10.39C6.49004 10.62 6.25004 10.75 6.00004 10.75Z'
                    fill='#868484'
                  />
                </svg>
                Filters
              </button>
              <div className='dropdown-menu dropdown-menu-end border-0 shadow filter-dropdown'>
                <div className='dropdown-item'>
                  <form action=''>
                    <div className='form-group'>
                      <label htmlFor=''>Sort List</label>
                      <select
                        id=''
                        className='form-control'
                        onChange={(e) => {
                          setSortOrder(e.target.value)
                        }}
                      >
                        <option value='DESC'>Descending Order</option>
                        <option value='ASC'>Ascending Order</option>
                      </select>
                    </div>
                    <div className='form-group mt-3'>
                      <label htmlFor=''>From</label>
                      <input
                        type='date'
                        className='form-control'
                        onChange={(e) => setFrom(e.target.value)}
                      />
                    </div>
                    <div className='form-group mt-3'>
                      <label htmlFor=''>To</label>
                      <input
                        type='date'
                        className='form-control'
                        onChange={(e) => setTo(e.target.value)}
                      />
                    </div>
                    <div className='form-group mt-3'>
                      <label htmlFor='posTerminalId'>POS Terminal ID</label>
                      <input
                        type='text'
                        className='form-control'
                        id='posTerminalId'
                        value={posTerminalId}
                        onChange={(e) => setPosTerminalId(e.target.value)}
                      />
                    </div>
                    <div className='form-group mt-3'>
                      <label htmlFor='provider'>Provider</label>
                      <select
                        className='form-control'
                        id='provider'
                        value={provider}
                        onChange={(e) => setProvider(e.target.value)}
                      >
                        <option value='ALL'>All</option>
                        <option value='NOMBA'>NOMBA</option>
                        <option value='MONIEPOINT'>MONIEPOINT</option>
                      </select>
                    </div>
                    <div className='row mt-3'>
                      <div className='col-6'>
                        <button
                          type='button'
                          className='btn btn-reset'
                          onClick={() => {
                            setSortOrder('DESC')
                            setFrom(undefined)
                            setTo(undefined)
                            setPosTerminalId(undefined)
                            setProvider(undefined)
                          }}
                        >
                          Reset
                        </button>
                      </div>
                      <div className='col-6 text-end'>
                        <button
                          type='button'
                          className='btn btn-filter'
                          onClick={() => fetchTransactions()}
                        >
                          Filter
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* <div className='card-header border-0 px-0'>
          <div className='row mt-2'>
            <div className='col-6 col-md-2 dropdown'>
              <button
                className='btn btn-filter dropdown-toggle'
                data-bs-toggle='dropdown'
                aria-expanded='false'
              >
                <svg
                  width='17'
                  height='17'
                  viewBox='0 0 24 24'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    d='M10.94 22.65C10.46 22.65 9.99002 22.53 9.55002 22.29C8.67002 21.8 8.14002 20.91 8.14002 19.91V14.61C8.14002 14.11 7.81002 13.36 7.50002 12.98L3.76002 9.02001C3.13002 8.39001 2.65002 7.31001 2.65002 6.50001V4.20001C2.65002 2.60001 3.86002 1.35001 5.40002 1.35001H18.6C20.12 1.35001 21.35 2.58001 21.35 4.10001V6.30001C21.35 7.35001 20.72 8.54001 20.13 9.13001L15.8 12.96C15.38 13.31 15.05 14.08 15.05 14.7V19C15.05 19.89 14.49 20.92 13.79 21.34L12.41 22.23C11.96 22.51 11.45 22.65 10.94 22.65ZM5.40002 2.85001C4.70002 2.85001 4.15002 3.44001 4.15002 4.20001V6.50001C4.15002 6.87001 4.45002 7.59001 4.83002 7.97001L8.64002 11.98C9.15002 12.61 9.65002 13.66 9.65002 14.6V19.9C9.65002 20.55 10.1 20.87 10.29 20.97C10.71 21.2 11.22 21.2 11.61 20.96L13 20.07C13.28 19.9 13.56 19.36 13.56 19V14.7C13.56 13.63 14.08 12.45 14.83 11.82L19.11 8.03001C19.45 7.69001 19.86 6.88001 19.86 6.29001V4.10001C19.86 3.41001 19.3 2.85001 18.61 2.85001H5.40002Z'
                    fill='#868484'
                  />
                  <path
                    d='M6.00004 10.75C5.86004 10.75 5.73004 10.71 5.60004 10.64C5.25004 10.42 5.14004 9.94999 5.36004 9.59999L10.29 1.69999C10.51 1.34999 10.97 1.23999 11.32 1.45999C11.67 1.67999 11.78 2.13999 11.56 2.48999L6.63004 10.39C6.49004 10.62 6.25004 10.75 6.00004 10.75Z'
                    fill='#868484'
                  />
                </svg>
                Filters
              </button>
              <div className='dropdown-menu dropdown-menu-end border-0 shadow filter-dropdown'>
                <div className='dropdown-item'>
                  <form action=''>
                    <div className='form-group'>
                      <label htmlFor=''>Sort List</label>
                      <select
                        id=''
                        className='form-control'
                        onChange={(e) => {
                          setSortOrder(e.target.value)
                        }}
                      >
                        <option value='DESC'>Descending Order</option>
                        <option value='ASC'>Ascending Order</option>
                      </select>
                    </div>
                    <div className='row mt-3'>
                      <div className='col-md-6'>
                        <button
                          type='button'
                          className='btn btn-reset'
                          onClick={() => setSortOrder('DESC')}
                        >
                          Reset
                        </button>
                      </div>
                      <div className='col-md-6 text-end'>
                        <button
                          type='button'
                          className='btn btn-filter'
                          onClick={() => setSortOrder('DESC')}
                        >
                          Filter
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div> */}
        <div className='card-body'>
          <div className='table-responsive'>
            <table className='table'>
              <thead>
                <tr>
                  <td>#</td>
                  <td>POS Terminal</td>
                  <td>Authorization Code</td>
                  <td>Customer Name</td>
                  <td>Customer MSISDN</td>
                  <td>Status</td>
                  <td>Date Added</td>
                  <td></td>
                </tr>
              </thead>
              <tbody>
                {loading && accounts.length === 0 && (
                  <tr>
                    <td colSpan={7} className='text-center'>
                      <HorizontalLoader />
                    </td>
                  </tr>
                )}
                {accounts.length > 0 &&
                  accounts.map((account: PosAccountType, index: Key) => (
                    <tr key={index}>
                      <td>{Number(index) + 1}</td>
                      <td>{account.posTerminalId}</td>
                      <td>{account.authorizationCode}</td>
                      <td>
                        {account.customer.firstName} {account.customer.lastName}
                      </td>
                      <td>{account.customer.msisdn}</td>
                      <td>{account.isActive ? 'Active' : 'Inactive'}</td>
                      <td>{TimeConverter(account.createdAt ?? '')}</td>

                      {/* <td>
                        <Link to={`/user/${account.customer.accountId}`}>
                          <button className='btn btn-sm btn-transaction-detail'>
                            <i className='d-icon d-eye-open'></i>
                          </button>
                        </Link> 
                      </td> */}

                      <td className='dropdown option-dropdown'>
                        <button
                          className='btn border-0 dropdown-toggle'
                          data-bs-toggle='dropdown'
                          aria-expanded='false'
                          data-bs-popper-config='{"strategy":"fixed"}'
                        >
                          <svg
                            width='18'
                            height='18'
                            viewBox='0 0 18 18'
                            fill='none'
                            xmlns='http://www.w3.org/2000/svg'
                          >
                            <path
                              d='M7.49991 8.4705C7.49991 7.72933 8.0955 7.14697 8.82344 7.14697C9.55138 7.14697 10.147 7.74256 10.147 8.4705C10.147 9.19844 9.55138 9.79403 8.82344 9.79403C8.0955 9.79403 7.49991 9.21168 7.49991 8.4705Z'
                              fill='#707070'
                            />
                            <path
                              d='M7.49991 14.1175C7.49991 13.3763 8.0955 12.7939 8.82344 12.7939C9.55138 12.7939 10.147 13.3895 10.147 14.1175C10.147 14.8454 9.55138 15.441 8.82344 15.441C8.0955 15.441 7.49991 14.8587 7.49991 14.1175Z'
                              fill='#707070'
                            />
                            <path
                              d='M7.49991 2.82353C7.49991 2.08235 8.0955 1.5 8.82344 1.5C9.55138 1.5 10.147 2.09559 10.147 2.82353C10.147 3.55147 9.55138 4.14706 8.82344 4.14706C8.0955 4.14706 7.49991 3.56471 7.49991 2.82353Z'
                              fill='#707070'
                            />
                          </svg>
                        </button>

                        <ul className='dropdown-menu dropdown-menu-end export-dropdown border-0 shadow'>
                          <li>
                            <Link
                              className='dropdown-item'
                              to={`/pos-terminal-transactions/${account.posTerminalId}`}
                            >
                              View POS transactions
                            </Link>
                          </li>
                          <li>
                            <Link
                              className='dropdown-item'
                              to={`/user/${account.customer.accountId}`}
                            >
                              View customer profile
                            </Link>
                          </li>
                          <li>
                            <button
                              type='button'
                              className='dropdown-item'
                              onClick={() => openUpdateTerminalModal(account)}
                            >
                              Update Terminal
                            </button>
                          </li>
                          <li>
                            <button
                              type='button'
                              className='dropdown-item'
                              onClick={() => deleteAccount(account.id)}
                            >
                              Delete POS
                            </button>
                          </li>
                        </ul>
                      </td>
                    </tr>
                  ))}
                {!loading && accounts.length === 0 && (
                  <tr>
                    <td colSpan={7} className='text-center'>
                      No accounts found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {paginationData.total >= paginationData.pageSize && (
            <div className='pagination w-100 d-flex justify-content-center'>
              {paginationData.total && (
                <Pagination
                  className='pagination-bar ps-0'
                  currentPage={Number(paginationData.pageNumber)}
                  totalCount={paginationData.total}
                  pageSize={paginationData.pageSize}
                  onPageChange={(page: React.SetStateAction<number>) => setPageNumber(page)}
                />
              )}
            </div>
          )}
        </div>
      </div>
      <ToastContainer></ToastContainer>
      <ModalWrapper
        showModal={showUpdateModal}
        closeModal={closeUpdateTerminalModal}
        title='Update Terminal'
        // size='lg'
        hideHeader={false}
      >
        <form
          className='posAccount__update-modal'
          onSubmit={(event) => {
            event.preventDefault()
            updateTerminalAuthorizationCode()
          }}
        >
          {updateError ? <div className='alert alert-danger'>{updateError}</div> : null}

          <div className='mb-3'>
            <label htmlFor='authorizationCode' className='form-label'>
              Authorization Code
            </label>
            <input
              id='authorizationCode'
              type='text'
              className='form-control'
              value={authorizationCode}
              onChange={(event) => setAuthorizationCode(event.target.value)}
              placeholder='Enter authorization code'
              disabled={updateLoading}
            />
          </div>

          <div className='posAccount__update-actions'>
            <button
              type='button'
              className='btn btn-cancel'
              onClick={closeUpdateTerminalModal}
              disabled={updateLoading}
            >
              Cancel
            </button>
            <button type='submit' className='btn btn-default' disabled={updateLoading}>
              {updateLoading ? 'Updating...' : 'Update'}
            </button>
          </div>
        </form>
      </ModalWrapper>
    </div>
  )
}

export default PosAccounts
